import { apiRequest } from '../../../shared/services/apiClient';

export interface EmailSnippet {
  id: string;
  name: string;
  shortcut: string | null;
  category: string | null;
  bodyHtml: string;
  updatedAt: string;
}

export interface EmailSnippetInput {
  name: string;
  shortcut: string;
  category: string;
  bodyHtml: string;
}

export interface EmailSnippetList {
  snippets: EmailSnippet[];
  variables: string[];
}

const BASE = '/api/v1/email/snippets';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const unwrap = (payload: unknown): Record<string, unknown> => {
  const record = asRecord(payload);
  return record.data && typeof record.data === 'object' ? asRecord(record.data) : record;
};

const optionalString = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);

export const normalizeEmailSnippet = (payload: unknown): EmailSnippet => {
  const data = unwrap(payload);
  return {
    id: String(data.id ?? ''),
    name: String(data.name ?? ''),
    shortcut: optionalString(data.shortcut),
    category: optionalString(data.category),
    bodyHtml: String(data.bodyHtml ?? ''),
    updatedAt: String(data.updatedAt ?? ''),
  };
};

export const listEmailSnippets = async (): Promise<EmailSnippetList> => {
  const data = unwrap(await apiRequest<unknown>(BASE));
  return {
    snippets: (Array.isArray(data.snippets) ? data.snippets : []).map(normalizeEmailSnippet),
    variables: (Array.isArray(data.variables) ? data.variables : []).map(String),
  };
};

export const createEmailSnippet = async (input: EmailSnippetInput): Promise<EmailSnippet> =>
  normalizeEmailSnippet(await apiRequest<unknown>(BASE, { method: 'POST', body: JSON.stringify(input) }));

export const updateEmailSnippet = async (id: string, input: EmailSnippetInput): Promise<EmailSnippet> =>
  normalizeEmailSnippet(await apiRequest<unknown>(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(input) }));

export const deleteEmailSnippet = async (id: string): Promise<void> => {
  await apiRequest<unknown>(`${BASE}/${id}`, { method: 'DELETE' });
};
